var _ = require("../../uam/module.js")(module.exports, {
  namespace: "egt.home",
  title: "EGT Home",
  description: "EGT house-brand home — Direction A 'Lobby' hero + cinematic scroll.",
  supportServer: true,
  fields: {
    reserveUrl: { title: "Reserve URL", type: "uri", description: "Reservation entry point.", required: false }
  }
});

var S = require("../helpers/egt.sections.js");
var FS = require("../helpers/egt.filmstudio.js");
var mediaInventory = require("../helpers/egt.media-inventory.js");

/* The EGT* Studio brand mark — "EGT" + a small scarce-gold star + " Studio".
 * The star is the locked brand asterisk (open founder question O-2 on its exact
 * meaning) rendered as a tasteful gold glyph, never a literal plain "*". It is
 * aria-hidden because the accessible name always carries the plain-text label.
 * Reused by the studios-panel row (labelNodes) and the carousel kicker. */
function studioStar() {
  return { n: "sup", c: "egt-home__star", "aria-hidden": "true", i: "*" };
}

/* The five rooms — rendered as the app-launcher rows in the studios panel
 * (renderStudiosPanel) AND mapped 1:1 to the ventures carousel. Each carries
 * its own canvas image (the carousel slide background) and a monogram key
 * (`mono`) for its launcher-row line-icon, reused from egt.about's monogram(). */
/* Each room carries BOTH its studios-panel row copy (title/line) and an optional
 * carousel-slide override (cTitle/cLine) so the two surfaces can speak
 * differently without drift. `mono` drives the inline currentColor launcher
 * glyph; only Universe keeps a real raster brand logo (`icon`) — Intrepid and
 * Studio now render inline marks (white at rest, gold on hover, palette-aware)
 * so the Intrepid mark is no longer black and Studio no longer reads as the EGT
 * logo. */
var ROOMS = [
  { k: "The Universe Platform", label: "Universe", title: "Run your whole business from one place.",
    href: "/platform", enter: "Enter Universe", line: "Sites, commerce, CRM, and content — one connected system under your brand.",
    cLine: "Build gorgeous websites that powerfully run your entire business — while protecting the privacy of your customers.",
    img: "/imgs/egt-platform-canvas.jpg", mono: "universe", icon: "/logo.svg" },
  { k: "Intrepid", label: "Intrepid", title: "A partner that works while you sleep.",
    href: "/intrepid", enter: "Meet Intrepid", line: "An AI that learns your business and quietly carries the hard parts.",
    cLine: "An ASI that learns your business and quietly carries the hard parts.",
    img: "/imgs/egt-intrepid-canvas.jpg", mono: "intrepid", icon: "/imgs/intrepid-mark.svg" },
  { k: "EGT Forge", label: "Forge", title: "We build, shoot, brand, and run the campaign.",
    href: "/forge", enter: "See the work", line: "A full creative agency — strategy, film, design, and the launch.",
    cTitle: "We don't just run campaigns — we build the brand.",
    img: "/imgs/egt-forge-canvas.jpg", mono: "forge" },
  /* PANEL-ONLY now: EGT* Studio (AI Film Studio). Its carousel appearance is the
   * bespoke EGT_STUDIO_SLIDE below (epic, placed right after Exploit), so this
   * room no longer carries carousel copy (cTitle/cLine). `label` stays a plain
   * string for the accessible name (aria-label / underline fallback);
   * `labelNodes` renders the gold-star wordmark in the row. */
  { k: "EGT Studio", label: "EGT Studio", labelNodes: [ S.egtMark(), " Studio" ],
    title: "Make your movie — the whole film, in your pocket.",
    href: "/studio", enter: "Enter the Studio", line: "Film Studio",
    img: "/imgs/egt-studio-hero.jpg", mono: "studio" },
  { k: "EGT Studios", label: "Entertainment", title: "The sound and the story of The Universe.",
    href: "/entertainment", enter: "Enter Studios", line: "Original music and entertainment, made under The Universe.",
    cTitle: "Emotionally Gripping Stories.",
    cLine: "Mind altering, human stories crafted by EGT with the latest technology.",
    img: "/imgs/egt-studios-canvas.jpg", mono: "stories" }
];

/* EXPLOIT — the book. Carousel-only (NOT one of the five ROOMS: it doesn't
 * belong in the Studios launcher panel, which is rooms/products, not the
 * book). Its slide uses BOTH its own art: the hero background as the slide
 * image, and the alpha title-card lockup in place of a typographic title. */
var EXPLOIT_VENTURE = {
  k: "Exploit — Book One", label: "Exploit",
  cLine: "A cinematic spiritual thriller. Book One of The Universe.",
  href: "/exploit", enter: "Read Exploit",
  img: "/imgs/exploit/scene-reyna-comes.jpg", titleCard: "/imgs/exploit/exploit-title-card.avif"
};

/* EGT* Studio — the AI Film Studio. A bespoke carousel-only slide (like
 * EXPLOIT_VENTURE), authored epic and placed immediately AFTER Exploit. It opts
 * into a poster-first video background (s.video) for cinematic impact, layered
 * over the /imgs/egt-studio-hero.jpg still that is also the reduced-motion
 * fallback (renderSlide + the base .egt-carousel__bg {img,video} rule handle
 * both). href is /studio — NEVER /intrepid, which would wrongly trigger the
 * living-husky rig. Copy from studio-overhaul/SYNTHESIS: one gold phrase only
 * ("yours."), coming-soon-honest, only Intrepid named. */
var EGT_STUDIO_SLIDE = {
  k: [ S.egtMark(), " Studio · AI Film Studio" ],
  cTitle: [ "The whole film in your head. Finally made — and ",
    { n: "span", c: "egt__gold", i: "yours." } ],
  cLine: "The connected AI film studio — one script becomes your characters, "
    + "your scenes, your final cut, the same faces from the first shot to the "
    + "last. Powered by Intrepid.",
  href: "/studio", enter: "Enter the Studio",
  img: "/imgs/egt-studio-hero.jpg", video: "/vis/identities.mp4"
};

/* THE JOURNEY OF THE COIN — the first film of the Universe. A bespoke
 * carousel-only slide (like EXPLOIT_VENTURE), placed right after Universe and
 * before the book it preludes. Links to the film page (player + making-of). */
var FILM_SLIDE = {
  k: [ S.egtMark(), " — A Short Film" ],
  cTitle: [ "The Journey of the ", { n: "span", c: "egt__gold", i: "Coin." } ],
  cLine: S.brand("A small token, seemingly insignificant, with a profound impact on time "
    + "and on life. The 3-minute-44 prelude to the EGT Universe, made with the EGT* "
    + "AI Film Studio — watch it, then read how it was made."),
  href: "/entertainment/the-journey-of-the-coin", enter: "Watch the film",
  img: "/imgs/coin/j06.jpg"
};

/* Carousel order — built EXPLICITLY (decoupled from the panel below): Universe ·
 * The Journey of the Coin · Exploit · EGT* Studio · Intrepid · Forge · Entertainment. Intrepid (ROOMS[1])
 * stays for its living-husky slide; ROOMS[3] (Studio) is intentionally DROPPED
 * here so EGT_STUDIO_SLIDE is the single, epic studio slide (no duplicate). */
var VENTURE_SLIDES = [ROOMS[0], FILM_SLIDE, EXPLOIT_VENTURE, EGT_STUDIO_SLIDE, ROOMS[1], ROOMS[2], ROOMS[4]];

/* The studios PANEL shows ROOMS as launcher rows but WITHOUT Intrepid (founder:
 * Intrepid is featured on its own surface, not this list; EGT* Studio takes the
 * featured slot). Filtering HERE — not by editing ROOMS — keeps the carousel's
 * Intrepid slide intact. Panel order: Universe · Forge · EGT* Studio ·
 * Entertainment. Both the row map and the paired hover-preview map iterate THIS
 * same array so their data-room indices stay in lockstep. */
var PANEL_ROOMS = ROOMS.filter(function (r) { return r.href !== "/intrepid"; });

/* The five inline-SVG monograms, REUSED from egt.about.js monogram() so all five
 * room rows carry a consistent stroked line-icon (Forge/Stories have no real
 * logo; this keeps the launcher cohesive). Single stroked figure on a 48-unit
 * grid, inheriting currentColor — no asset deps. */
function monogram(key) {
  /* most marks live on a 48-grid; the real Intrepid mark is authored on a 64-grid */
  var vb = key === "intrepid" ? "0 0 64 64" : "0 0 48 48";
  var base = { n: "svg", c: "egt-launch__mono", viewBox: vb, "aria-hidden": "true", focusable: "false" };
  /* shared stroke style for the Intrepid network mark (recolored to currentColor
   * from the source #000 so it inherits the launcher color — white, gold hover) */
  var iStroke = { fill: "none", stroke: "currentColor", "stroke-width": "2.25", "stroke-linecap": "round", "stroke-linejoin": "round" };
  function iPath(d) { return Object.assign({ n: "path", d: d }, iStroke); }
  function iNode(cx, cy) { return { n: "circle", cx: cx, cy: cy, r: "2.5", fill: "currentColor", stroke: "none" }; }
  var paths = {
    /* Universe — an orbit ring with a centre point (a whole system) */
    universe: [
      { n: "circle", cx: "24", cy: "24", r: "3.2", fill: "currentColor", stroke: "none" },
      { n: "ellipse", cx: "24", cy: "24", rx: "17", ry: "7.5", fill: "none", stroke: "currentColor", "stroke-width": "1.5" },
      { n: "ellipse", cx: "24", cy: "24", rx: "7.5", ry: "17", fill: "none", stroke: "currentColor", "stroke-width": "1.5" }
    ],
    /* Intrepid — the REAL Intrepid mark (network of nodes around a centre
     * diamond), recolored to currentColor so it is no longer hard black. */
    intrepid: [
      iPath("M13.75,32h10"), iPath("M17.75,24h8"), iPath("M17.75,40h8"),
      iPath("M27.75,32l6-12"), iPath("M27.75,32l6,12"),
      iPath("M47.75,32l-6-12"), iPath("M47.75,32l-6,12"),
      iPath("M33.75,20h8"), iPath("M33.75,44h8"),
      iPath("M37.75,24l8,8-8,8-8-8,8-8Z"),
      iNode("27.75", "32"), iNode("33.75", "20"), iNode("41.75", "20"),
      iNode("47.75", "32"), iNode("41.75", "44"), iNode("33.75", "44")
    ],
    /* Forge — an anvil-like wedge over a baseline (where things are made) */
    forge: [
      { n: "path", d: "M12 19h18a8 8 0 01-8 8h-2l3 7", fill: "none", stroke: "currentColor", "stroke-width": "1.8", "stroke-linecap": "round", "stroke-linejoin": "round" },
      { n: "path", d: "M14 34h12", fill: "none", stroke: "currentColor", "stroke-width": "1.8", "stroke-linecap": "round" },
      { n: "path", d: "M30 19l4-4", fill: "none", stroke: "currentColor", "stroke-width": "1.8", "stroke-linecap": "round" }
    ],
    /* Studio — a film clapperboard (make your movie) */
    studio: [
      { n: "rect", x: "10", y: "22", width: "28", height: "16", rx: "2", fill: "none", stroke: "currentColor", "stroke-width": "1.6" },
      { n: "path", d: "M10 22L12 15L40 15L38 22Z", fill: "none", stroke: "currentColor", "stroke-width": "1.6", "stroke-linejoin": "round" },
      { n: "path", d: "M18 15l-2 7M26 15l-2 7M34 15l-2 7", fill: "none", stroke: "currentColor", "stroke-width": "1.4", "stroke-linecap": "round" }
    ],
    /* Stories — a play-triangle inside a frame (the sound and the story) */
    stories: [
      { n: "rect", x: "8", y: "10", width: "32", height: "28", rx: "3", fill: "none", stroke: "currentColor", "stroke-width": "1.5" },
      { n: "path", d: "M21 18l9 6-9 6z", fill: "currentColor", stroke: "none" }
    ]
  };
  base.i = paths[key] || paths.universe;
  return base;
}

/* the list<->card view toggle lives in egt.sections.js (S.viewToggle) — shared
 * with /studios. Client behavior + the intro demo are in egt.home.js (client). */



exports.render = async function render(options) {
  var reserveUrl = (options && options.reserveUrl) || "/reserve";
  /* No nav CTA on the homepage (founder 2026-10-24: "Join the Beta" only where it's
   * tastefully needed — the platform pages). The bar keeps login, contact and the
   * menu; the hero carries the one ask, Request Access. */
  return S.page(reserveUrl, [
    homeSchema(),
    renderFilmBar(),
    renderHero(),
    renderNewFromStudio(),
    renderStudiosPanel(),
    renderDuality(),
    renderCarousel(),
    renderListen(),
    renderCapture(),
    FS.script()
  ], {});
};

/* MORE FROM THE STUDIO (spec 35 WP4; redesigned 2026-10-14, founder: journal first, a
 * little podcast) — over a gym photo (building, rep by rep): a header card, the latest
 * journal entry as a big frosted feature card, the next two entries, then the newest
 * episode of each podcast (Classic Man first, then Create_) as compact cards. The homepage is pre-built, so the cards are filled
 * in the browser from /api/content/latest (/src/egt.latest.js). The section ships
 * HIDDEN and appears only with real data; each card stays hidden until it has some. */
function renderNewFromStudio() {
  var arrow = { n: "span", c: "egt-hn__arrow", "aria-hidden": "true", i: "→" };
  return {
    n: "section", id: "egt-home-new", c: "egt-section egt-canvas egt-home-new egt-hn",
    hidden: "hidden", "aria-label": "More from the studio",
    i: [
      S.media({ img: "/imgs/podcasts/new-from-the-studio-victor-freitas.jpg", alt: "", parallax: 0.06 }),
      { n: "div", c: "egt__shell egt-canvas__inner egt-hn__inner", i: [
        { n: "div", c: "egt-glass egt-hn__head egt-reveal", i: [
          { n: "div", c: "egt-hn__eyebrow", i: "From the studio" },
          { n: "h2", c: "egt-hn__title", i: [ "More from the ", { n: "em", i: "studio" } ] },
          { n: "p", c: "egt-hn__line", i: "The latest from EGT: the founder's journal and our podcasts." },
          { n: "span", c: "egt-hn__links", i: [
            { n: "a", c: "egt-hn__all", href: "/journal", i: [ "The Making ", arrow ] },
            { n: "a", c: "egt-hn__all", href: "/podcasts", i: [ "All podcasts ", arrow ] } ] }
        ] },
        { n: "a", c: "egt-glass egt-hn__feature egt-reveal egt-reveal--up", href: "/journal", "data-egt-latest": "journal", hidden: "hidden", i: [
          { n: "span", c: "egt-hn__art", i: { n: "img", c: "egt-hn__img", src: "/imgs/podcasts/new-from-the-studio-victor-freitas-thumb.jpg", alt: "", loading: "lazy", decoding: "async" } },
          { n: "span", c: "egt-hn__body", i: [
            { n: "span", c: "egt-hn__kicker", "data-f": "kicker", i: "Latest entry" },
            { n: "span", c: "egt-hn__ftitle", "data-f": "title", i: "" },
            { n: "span", c: "egt-hn__dek", "data-f": "dek", i: "" },
            { n: "span", c: "egt-hn__meta", "data-f": "meta", i: "" },
            { n: "span", c: "egt-hn__next", "data-f-next": "", hidden: "hidden", i: [
              { n: "span", c: "egt-hn__nextk", i: [ { n: "span", c: "egt-hn__live", "aria-hidden": "true" }, "Next entry in" ] },
              { n: "span", c: "egt-hn__cd", i: [ [ "d", "days" ], [ "h", "hours" ], [ "m", "min" ], [ "s", "sec" ] ].map(function (u) {
                return { n: "span", c: "egt-hn__well", i: [ { n: "b", c: "egt-cdv", "data-u": u[0], i: "00" }, { n: "small", i: u[1] } ] };
              }) },
              { n: "span", c: "egt-hn__nextd", "data-f": "nextday", i: "" } ] },
            { n: "span", c: "egt-hn__btn", i: [ "Read the entry ", arrow ] }
          ] }
        ] },
        { n: "div", c: "egt-hn__side", i: [
          { n: "div", c: "egt-glass egt-hn__more egt-reveal egt-reveal--up", "data-egt-latest": "more", hidden: "hidden", i: [
            { n: "div", c: "egt-hn__eyebrow", i: "Also in The Making" },
            { n: "div", c: "egt-hn__list", "data-f": "list" }
          ] },
          { n: "a", c: "egt-glass egt-hn__pod egt-reveal egt-reveal--up", href: "/podcasts", "data-egt-latest": "episode-0", hidden: "hidden", i: [
            { n: "span", c: "egt-hn__podart", i: { n: "img", c: "egt-hn__img", src: "/imgs/podcasts/create-cover.jpg", alt: "", loading: "lazy", decoding: "async" } },
            { n: "span", c: "egt-hn__podbody", i: [
              { n: "span", c: "egt-hn__kicker", "data-f": "kicker", i: "New episode" },
              { n: "span", c: "egt-hn__podtitle", "data-f": "title", i: "" },
              { n: "span", c: "egt-hn__meta", "data-f": "meta", i: "" } ] }
          ] },
          { n: "a", c: "egt-glass egt-hn__pod egt-reveal egt-reveal--up", href: "/podcasts", "data-egt-latest": "episode-1", hidden: "hidden", i: [
            { n: "span", c: "egt-hn__podart", i: { n: "img", c: "egt-hn__img", src: "/imgs/podcasts/create-cover.jpg", alt: "", loading: "lazy", decoding: "async" } },
            { n: "span", c: "egt-hn__podbody", i: [
              { n: "span", c: "egt-hn__kicker", "data-f": "kicker", i: "New episode" },
              { n: "span", c: "egt-hn__podtitle", "data-f": "title", i: "" },
              { n: "span", c: "egt-hn__meta", "data-f": "meta", i: "" } ] }
          ] },
          { n: "a", c: "egt-glass egt-hn__podall egt-reveal egt-reveal--up", href: "/podcasts", i: [ "All podcasts ", arrow ] }
        ] }
      ] },
      { n: "script", src: "/src/egt.countdown.js", type: "text/javascript", defer: "defer" },
      { n: "script", src: "/src/egt.latest.js", type: "text/javascript", defer: "defer" }
    ]
  };
}

/* FILM BAR — a slim one-liner directly under the nav. It used to point at the
 * Exploit microsite ("Looking for Exploit?"); founder directive 2026-10-05
 * swapped it for the first film of the Universe, The Journey of the Coin. Exploit
 * stays reachable from the hero CTA and the carousel. Wraps to two lines on a
 * phone; the whole bar links to the film page. */
function renderFilmBar() {
  return {
    n: "a", href: "/entertainment/the-journey-of-the-coin", c: "egt-home-xbar",
    style: "display:block;text-align:center;text-decoration:none;padding:.6rem 1.25rem;" +
      "background:rgba(196,142,47,.10);border-bottom:.03125rem solid rgba(196,142,47,.28);" +
      "color:var(--egt-hi,#ededef);font-size:.9rem;line-height:1.45;letter-spacing:.01em;",
    i: [ { n: "em", i: "The Journey of the Coin" }, " \u2014 Short Film. ",
      { n: "span", c: "egt__gold", style: "color:var(--egt-gold,#c48e2f);font-weight:600;",
        i: "Watch it now" },
      { n: "span", "aria-hidden": "true", style: "color:var(--egt-gold,#c48e2f);", i: " →" } ]
  };
}

/* CAPTURE — WEB-2. The home surface's own door, sitting directly under Listen
 * so the album drop and the sign-up are one continuous beat: hear it, then be
 * told when the next one lands. Frosted card over undimmed brand imagery
 * (agents.md §4 — no scrim). source "home" → the platform newsletter. */
function renderCapture() {
  return {
    n: "section", c: "egt-section egt-canvas egt-canvas--center", id: "egt-home-capture",
    i: [
      S.media({ img: "/imgs/egt-home-aux.jpg", alt: "", parallax: 0.08 }),
      { n: "div", c: "egt__shell egt-canvas__inner egt-reveal",
        i: S.capture({
          source: "home",
          id: "egt-cap-home",
          variant: "panel",
          eyebrow: "EGT",
          heading: "h2",
          title: [ "Be first to ", { n: "span", c: "egt__gold", i: "know." } ],
          lead: "Everything from EGT — new music and the book, the Platform and Studio, the agency, and what we make next. One list, no noise, and we confirm your address before we ever send you anything.",
          label: "Your email address",
          cta: "Notify me",
          /* Marketing-list consent, REQUIRED + never pre-checked (founder ruling
           * 2026-07-22). Home is a list-adding capture; default label. */
          consent: true
        }) }
    ]
  };
}

/* Per-surface JSON-LD: WebPage + BreadcrumbList for the home surface. The
 * site-wide Organization + WebSite graph is emitted separately by S.page (see
 * S.siteSchema). URLs are built from S.siteOrigin() so they follow the
 * production domain automatically. Home's WebPage is "about" the Organization
 * entity; the breadcrumb is a single Home node. */
function homeSchema() {
  var O = S.siteOrigin();
  return S.jsonLd([
    { "@type": "WebPage", "@id": O + "/#webpage", url: O + "/",
      name: "EGT* Film Studio — Tell the story, the way you want to.",
      description: "EGT* Film Studio, powered by Intrepid: script, characters, scenes and cinema-ready video in one connected studio. Public beta.",
      isPartOf: { "@id": O + "/#website" },
      about: { "@id": O + "/#organization" },
      breadcrumb: { "@id": O + "/#breadcrumb" } },
    { "@type": "BreadcrumbList", "@id": O + "/#breadcrumb",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: O + "/" }
      ] }
  ]);
}

/* HERO — EGT* Film Studio (founder 2026-10-24). The homepage now leads with the
 * new product: a loop of real scenes from the EGT* intro and one ask, Request
 * Access, which hands the email to the platform reserve wizard. The old
 * "Welcome to The Universe / The magic and the machinery" hero card was dropped
 * (founder: "Drop it"); the studios panel below still links every pillar. */
function renderHero() {
  return FS.hero({
    id: "egt-home-hero", loop: "film-home",
    title: [ "Tell the story, ", { n: "br", c: "d-none d-md-inline" }, "the way ", { n: "span", c: "egt__gold", i: "you want to." } ],
    next: "#egt-home-new",
    /* the way into /studio, kept quieter than the ask (founder 2026-10-29) */
    tour: { href: "/studio", t: "Tour the Studio", sub: "The films, the booth and how it works",
      thumb: "/vis/film-studio/card-intro-wide.jpg" }
  });
}

/* STUDIOS PANEL — the five rooms, lifted out of the hero into their OWN
 * full-screen panel. The panel is an .egt-canvas (full-bleed approved imagery,
 * UNDIMMED — contrast comes from frosted glass only, never a scrim). Floating
 * CENTERED inside it: a frosted-glass "browser / app-window" CARD that holds the
 * rooms as app-launcher ROWS. The card is bounded (max-width) so the panel bg
 * shows around it — the card lives IN the panel, it is NOT the panel.
 *
 * G-1: nothing here is position:sticky, so wrapping in .egt-canvas is safe.
 * The card chrome: three muted traffic-light dots on the LEFT, a subtext title
 * on the RIGHT. Each row = whole-element link [ monogram ][ serif name ][ sub ]
 * with a gold-accent hover/focus (rgba(var(--egt-gold-rgb),…)), underline draw,
 * selected-app row highlight, visible :focus-visible ring, staggered reveal. */
function renderStudiosPanel() {
  return {
    n: "section", id: "egt-studios-panel",
    c: "egt-section egt-canvas egt-canvas--center egt-studios-panel",
    i: [
      /* full-bleed media background — UNDIMMED (no scrim); the radial edge
       * feather lives on .egt-canvas__media::after in _egt.scss. A DEFAULT bg
       * (egt-studios-panel-bg — its OWN file, split from the Stories/Studios
       * room art so either can be swapped independently via the media manager)
       * stays visible when idle; one preview-img per room (r.img) crossfades in
       * BEHIND the frosted card when its row is hovered/focused — re-applying
       * the beloved old hero :has() crossfade. */
      { n: "div", c: "egt-canvas__media", "aria-hidden": "true",
        i: [
          { n: "img", c: "egt-canvas__img egt-studios-panel__bg", src: "/imgs/egt-studios-panel-bg.jpg",
            alt: "", loading: "lazy", decoding: "async" }
        ].concat(PANEL_ROOMS.map(function (r, idx) {
          return { n: "img", c: "egt-canvas__img egt-studios-panel__preview-img",
            "data-room": String(idx), src: r.img,
            alt: "", loading: "lazy", decoding: "async" };
        })) },
      /* content layer — the browser card floats centered, bounded.
       * data-egt-view (list|card) + data-egt-view-root mark this as the surface
       * the toggle controls; data-egt-view-intro plays the one-time list->card->
       * list demo on first view (egt.home.js). Rests in LIST view. */
      { n: "div", c: "egt-canvas__inner egt-studios-panel__inner",
        i: { n: "div", c: "egt-glass egt-launch egt-reveal egt-lift", "data-egt-tilt": "true",
          "data-egt-view": "list", "data-egt-view-root": "true", "data-egt-view-intro": "true",
          i: [
            /* window chrome: traffic-lights LEFT, [toggle][title] RIGHT */
            { n: "div", c: "egt-launch__bar",
              i: [
                { n: "span", c: "egt-launch__lights", "aria-hidden": "true",
                  i: [
                    { n: "span", c: "egt-launch__dot egt-launch__dot--r" },
                    { n: "span", c: "egt-launch__dot egt-launch__dot--y" },
                    { n: "span", c: "egt-launch__dot egt-launch__dot--g" }
                  ] },
                { n: "span", c: "egt-launch__barside",
                  i: [ { n: "span", c: "egt-launch__title", i: "The Studios" }, S.viewToggle("list") ] }
              ] },
            /* body: the five rooms — app-launcher rows in list view, art cards in
             * card view. data-egt-view-body is the layer the crossfade morph fades. */
            { n: "nav", c: "egt-launch__body", "aria-label": "The studios", "data-egt-view-body": "true",
              i: PANEL_ROOMS.map(function (r, idx) {
                return {
                  n: "a", href: r.href,
                  c: "egt-launch__row egt-reveal egt-reveal--up",
                  style: "--egt-i:" + idx,
                  "data-room": String(idx),
                  "aria-label": r.label + " — " + r.line,
                  i: [
                    /* card-view art thumb (hidden in list view) */
                    { n: "span", c: "egt-launch__thumb", "aria-hidden": "true",
                      i: { n: "img", c: "egt-launch__thumb-img", src: r.img, alt: "", loading: "lazy", decoding: "async" } },
                    { n: "span", c: "egt-launch__icon", "aria-hidden": "true",
                      i: r.icon
                        ? { n: "img", c: "egt-launch__img", src: r.icon, alt: "", loading: "lazy", decoding: "async" }
                        : monogram(r.mono) },
                    { n: "span", c: "egt-launch__id",
                      i: [
                        /* No ™ here: the trademark is the Intrepid DESIGN
                         * mark (the icon just above), not the word — see
                         * egt.intrepid.js's hero, where the ™ sits on the
                         * living avatar itself. The footer's site-wide
                         * "Intrepid™ ... used under license" notice already
                         * covers the legal requirement. */
                        { n: "span", c: "egt-launch__name egt-underline", i: r.labelNodes || r.label },
                        { n: "span", c: "egt-launch__sub", i: r.line }
                      ] },
                    { n: "span", c: "egt-launch__go", "aria-hidden": "true", i: "›" }
                  ]
                };
              }) }
          ] } }
    ]
  };
}

/* DUALITY — a PINNED, scroll-scrubbed two-beat (the signature moment).
 *
 * Structure (heed Gotcha G-1): a PLAIN tall wrapper (.egt-duality-stage, NOT an
 * .egt-canvas) holds a sticky inner stage pinned at 100vh. As you scroll the
 * stage, a single 0..1 progress (--egt-dp, set on the stage by
 * egt.home.scroll.js) crossfades the serif word "The magic." -> "The
 * machinery.", crossfades the background art (cosmic -> systems), and wipes a
 * gold rail left->right. The media is the ONLY clipped layer (.egt-dstage__media
 * has overflow:hidden) — the sticky element's scroll-ancestor chain stays clean
 * (no overflow:hidden / transform / filter / clip on any ancestor), so sticky
 * actually pins. Reduced motion = both beats rendered static & stacked. */
function renderDuality() {
  return {
    n: "div", id: "egt-duality", c: "egt-duality-stage", "data-egt-duality": "true",
    i: { n: "div", c: "egt-dstage__pin",
      i: [
        /* media layer — the ONLY overflow:hidden element; never an overlay */
        { n: "div", c: "egt-dstage__media", "aria-hidden": "true",
          i: [
            { n: "img", c: "egt-dstage__art egt-dstage__art--magic",
              src: "/imgs/egt-home-aux.jpg", alt: "", loading: "lazy", decoding: "async" },
            { n: "img", c: "egt-dstage__art egt-dstage__art--machinery",
              src: "/imgs/egt-machinery.jpg", alt: "", loading: "lazy", decoding: "async" }
          ] },
        /* content layer — centered statement, two crossfading beats */
        { n: "div", c: "egt__shell egt-dstage__inner",
          i: { n: "div", c: "egt-glass egt-glass--wide egt-dstage__card",
            i: [
              { n: "p", c: "egt-dstage__idx",
                i: [ { n: "span", c: "egt-dstage__idx-a", i: "EGT" },
                  { n: "span", c: "egt-dstage__idx-b", i: "EGT" } ] },
              /* the giant serif word crossfades between the two beats */
              { n: "div", c: "egt-dstage__words",
                i: [
                  { n: "h2", c: "egt-dstage__word egt-dstage__word--magic", i: "The magic." },
                  { n: "h2", c: "egt-dstage__word egt-dstage__word--machinery", "aria-hidden": "true", i: "The machinery." }
                ] },
              /* the gold rail that wipes left -> right with progress */
              { n: "div", c: "egt-dstage__rail", "aria-hidden": "true",
                i: { n: "span", c: "egt-dstage__rail-fill" } },
              { n: "div", c: "egt-dstage__copies",
                i: [
                  { n: "p", c: "egt-dstage__copy egt-dstage__copy--magic",
                    i: "Unforgettable experiences for brands through The Forge — and deeply emotional stories of our own — crafted with modern technology and top-of-the-line production." },
                  { n: "p", c: "egt-dstage__copy egt-dstage__copy--machinery", "aria-hidden": "true",
                    i: "The platform and the AI partner that quietly carry the hard parts, so the magic ships." }
                ] },
              { n: "p", c: "egt-dstage__tags",
                i: [
                  { n: "span", c: "egt-dstage__tag egt-dstage__tag--magic", i: "Entertainment · Studio · Forge" },
                  { n: "span", c: "egt-dstage__tag egt-dstage__tag--machinery", "aria-hidden": "true", i: "Platform · Intrepid  · Namespace" }
                ] }
            ] } }
      ] }
  };
}

/* VENTURES — carousel of frosted-glass-over-imagery cards. On desktop each slide
 * is an editorial split (art breathes left, glass content right) with one extra
 * line of substance; on narrow screens it falls back to the bottom-anchored
 * glass. Locked: this stays a CAROUSEL (not text-only). */
function renderCarousel() {
  return {
    n: "section", c: "egt-section",
    i: { n: "div", c: "egt__shell egt-carousel egt-carousel--wide egt-reveal", id: "egt-carousel", "data-egt-carousel": "true",
      i: [
        { n: "div", c: "egt-carousel__viewport",
          i: { n: "div", c: "egt-carousel__track", id: "egt-carousel-track", i: VENTURE_SLIDES.map(renderSlide) } },
        { n: "div", c: "egt-carousel__controls",
          i: [
            { n: "div", c: "egt-dots", id: "egt-carousel-dots", "aria-label": "Slides" },
            { n: "div", c: "egt-arrows",
              i: [
                { n: "button", type: "button", id: "egt-carousel-prev", "aria-label": "Previous", i: "‹" },
                { n: "button", type: "button", id: "egt-carousel-next", "aria-label": "Next", i: "›" }
              ] }
          ] }
      ] }
  };
}

function renderSlide(s) {
  /* a titleCard (the book's alpha lockup art) replaces the typographic title
   * with the real designed title graphic — used only by the Exploit slide. */
  var titleNode = s.titleCard
    ? { n: "img", c: "egt-card__title-img", src: s.titleCard, alt: s.k, loading: "lazy", decoding: "async" }
    : { n: "h3", c: "egt-card__title", i: s.cTitle || s.title };
  /* the Intrepid slide gets the living husky rig to the LEFT of its text card
   * (two-part layout; stacks on mobile). The rig is mounted client-side by the
   * shared intrepid-rig.js module (declared in index.json BEFORE egt.home.js);
   * it hydrates every [data-egt-rig-mount] and is idempotent, so a second load
   * never double-defines. The .egt-intrepid-rig wrapper carries the rig's brand
   * tokens + chassis CSS (compiled site-wide via index.scss). Reduced-motion is
   * handled by the rig's own SCSS (static husky). */
  var isIntrepid = s.href === "/intrepid";
  var card = {
    n: "div", c: "egt-glass egt-carousel__card"
      + (s.titleCard ? " egt-carousel__card--book" : "")
      + (isIntrepid ? " egt-carousel__card--rig" : ""),
    i: [
      { n: "p", c: "egt-card__k", i: s.k },
      titleNode,
      { n: "p", c: "egt-card__copy", i: s.cLine || s.line },
      { n: "a", href: s.href, c: "egt-cta egt-cta--text",
        i: [ s.enter, { n: "span", c: "egt-cta__tick", i: " ›" } ] }
    ]
  };
  /* Background: a still <img> by default. A slide may opt into a poster-first
   * muted/looping video (s.video) — the EGT* Studio slide does, for cinematic
   * impact. The video is layered OVER the same still (used as its <img> poster),
   * so prefers-reduced-motion — which hides the <video> via SCSS — falls back to
   * the poster still. The base .egt-carousel__bg {img,video} rule already gives
   * both object-fit:cover, so this does not disturb the img-only slides. */
  var bg = s.video
    ? { n: "div", c: "egt-carousel__bg egt-carousel__bg--video",
        i: [
          { n: "img", c: "egt-carousel__poster", src: s.img, alt: "", loading: "lazy", decoding: "async" },
          { n: "video", c: "egt-carousel__video", autoplay: true, muted: true, loop: true, playsinline: true,
            poster: s.img, "aria-hidden": "true",
            i: { n: "source", src: s.video, type: "video/mp4" } }
        ] }
    : { n: "div", c: "egt-carousel__bg",
        i: { n: "img", src: s.img, alt: "", loading: "lazy", decoding: "async" } };
  var children = [ bg ];
  if (isIntrepid) {
    children.push({
      n: "div", c: "egt-carousel__rig egt-intrepid-rig",
      i: { n: "div", c: "egt-carousel__rig-mount",
        "data-egt-rig-mount": "true", "data-egt-rig-mood": "ready",
        role: "img", "aria-label": "Intrepid — a living husky that follows your cursor" }
    });
  }
  children.push(card);
  return {
    n: "div", c: "egt-carousel__slide" + (isIntrepid ? " egt-carousel__slide--rig" : ""),
    i: children
  };
}

/* LISTEN — the sound of the Universe (founder 2026-10-26; HOMEPAGE ONLY — the
 * album rows stay on /entertainment). One frosted card over an undimmed frame
 * from the EGT* intro (the busker, AX40), and four large platform tiles that go
 * straight to the house artist "The Universe, EGT" on each service (verified
 * 2026-10-26). Platform marks: Simple Icons (CC0), app/public/imgs/platforms. */
var ARTIST_PAGES = [
  { k: "applemusic", nm: "Apple Music", href: "https://music.apple.com/us/artist/the-universe-egt/1858007635" },
  { k: "spotify", nm: "Spotify", href: "https://open.spotify.com/artist/0GrnoT0c911oBpvHqbTawa" },
  { k: "youtubemusic", nm: "YouTube Music", href: "https://music.youtube.com/channel/UCtrNIgPSQr8MfrYuOtMA7vg" },
  { k: "amazonmusic", nm: "Amazon Music", href: "https://music.amazon.com/artists/B0G4SF31R8/the-universe-egt" }
];

function renderListen() {
  return {
    n: "section", c: "egt-section egt-canvas egt-canvas--center egt-sound", id: "egt-home-listen",
    i: [
      S.media({ img: "/vis/film-studio/still-ax40.jpg", alt: "", parallax: 0.06 }),
      { n: "div", c: "egt__shell egt-canvas__inner egt-sound__inner",
        i: { n: "div", c: "egt-glass egt-sound__card egt-reveal",
          i: [
            { n: "p", c: "egt__eyebrow", i: "The Universe, EGT" },
            { n: "h2", c: "egt-sound__title", i: [ "Listen to the sound of ", { n: "span", c: "egt__gold", i: "the Universe." } ] },
            { n: "p", c: "egt-sound__lead", i: "Every EGT album, score and single, on the platform you already use. Follow the artist and every new release lands in your library." },
            { n: "ul", c: "egt-sound__grid",
              i: ARTIST_PAGES.map(function (p, i) {
                return { n: "li", c: "egt-sound__item",
                  i: { n: "a", c: "egt-sound__tile egt-reveal egt-reveal--up", style: "--egt-i:" + i,
                    href: p.href, target: "_blank", rel: "noopener",
                    "aria-label": "The Universe, EGT on " + p.nm + " (opens in a new tab)",
                    i: [
                      { n: "img", c: "egt-sound__logo", src: "/imgs/platforms/" + p.k + ".svg", alt: "", loading: "lazy", decoding: "async" },
                      { n: "span", c: "egt-sound__nm", i: p.nm },
                      { n: "span", c: "egt-sound__go", i: [ "Listen", { n: "span", c: "egt-cta__tick", "aria-hidden": "true", i: " ›" } ] }
                    ] } };
              }) },
            { n: "p", c: "egt-sound__more", i: [ "Every album, one place: ", { n: "a", href: "/entertainment#wall", i: "the music of the Universe" }, "." ] }
          ] } }
    ]
  };
}
